import { createSelector, DefaultProjectorFn, MemoizedSelector } from '@ngrx/store';

import {
  DashboardDevice,
  DashboardInstallationData,
  InstallationItemGatewayDevice,
  OptimizationRecommendationsType,
  ViguideRecommendationData,
  ViguideRecommendations,
} from '@vi-iot/client';

import { AppState } from '../app.state';
import { AuthSelectors } from '../auth/auth.selectors';
import { DeviceDetails, InstallationDetailsSelectors } from '../installation-details';
import { InstallationsSelectors } from '../installations';

import {
  InstallationWithRecommendation,
  OptimizationRecommendationSlug,
  OptimizationRecommendationSpreaded,
} from './viguide-recommendations.model';
import { ViguideRecommendationsState } from './viguide-recommendations.state';

export namespace ViguideRecommendationsSelectors {
  export const viguideRecommendationsState = (state: AppState): ViguideRecommendationsState =>
    state.viguideRecommendations;

  export const getOptimizationRecommendations = createSelector(
    viguideRecommendationsState,
    (state: ViguideRecommendationsState): ViguideRecommendations[] =>
      state.optimizationRecommendations
  );

  export const selectProInstallations = createSelector(
    viguideRecommendationsState,
    (state: ViguideRecommendationsState): DashboardInstallationData[] => state.proInstallations
  );

  export const isAnyOptimizationRecommendationAvailable = createSelector(
    viguideRecommendationsState,
    AuthSelectors.isRsaUser(),
    (state: ViguideRecommendationsState, isRsaUser: boolean): boolean =>
      state.optimizationRecommendations.length > 0 && !isRsaUser
  );

  export const getOptimizationRecommendationsForSelectedBoiler = createSelector(
    getOptimizationRecommendations,
    InstallationDetailsSelectors.getDevice(),
    (
      optimizations: ViguideRecommendations[],
      { boilerSerial }: DeviceDetails
    ): OptimizationRecommendationsType[] => {
      const foundOptimization = optimizations.find(
        (optimization) => optimization.boilerSerial === boilerSerial
      );

      return foundOptimization
        ? foundOptimization.recommendations.map((recommendation) => recommendation.name)
        : [];
    }
  );

  export const getIgnoredRecommendationTilesSettings = createSelector(
    viguideRecommendationsState,
    InstallationDetailsSelectors.getDevice(),

    (
      state: ViguideRecommendationsState,
      { boilerSerial }: DeviceDetails
    ): OptimizationRecommendationSlug[] =>
      state.ignoredTilesSettings && Object.keys(state.ignoredTilesSettings).length > 0
        ? state.ignoredTilesSettings[boilerSerial]
        : []
  );

  export function getAvailableRecommendationsForTile(
    expectedRecommendations: OptimizationRecommendationsType[],
    ignoredTileName: OptimizationRecommendationSlug
  ): MemoizedSelector<
    AppState,
    OptimizationRecommendationsType[],
    DefaultProjectorFn<OptimizationRecommendationsType[]>
  > {
    return createSelector(
      getOptimizationRecommendationsForSelectedBoiler,
      getIgnoredRecommendationTilesSettings,
      (
        recommendations: OptimizationRecommendationsType[],
        ignoredTiles: OptimizationRecommendationSlug[]
      ): OptimizationRecommendationsType[] => {
        if (ignoredTiles.includes(ignoredTileName)) {
          return [];
        }

        return recommendations.filter((recommendation) =>
          expectedRecommendations.includes(recommendation)
        );
      }
    );
  }

  /**
   * Pro installations fetched for /dashboard view
   * All installations is left to support old installation list
   */
  export const installationsWithRecommendations = createSelector(
    selectProInstallations,
    InstallationsSelectors.selectInstallations(),
    getOptimizationRecommendations,
    (proInstallations, allInstallations, optimizations): InstallationWithRecommendation[] =>
      optimizations.reduce((acc, optimization) => {
        let installationData: Partial<InstallationWithRecommendation>;

        // Dashboard view loop
        if (proInstallations.length > 0) {
          let matchingDevice: DashboardDevice;

          const matchingInstallation = proInstallations.find((installation) => {
            const device = installation.devices.find(
              (device) => device.vin === optimization.boilerSerial
            );

            matchingDevice = device;

            return !!device;
          });

          if (matchingInstallation) {
            const address = matchingInstallation.address;

            let location = `${address.street}` + ` ${address.houseNumber}`;

            if (!!address.zip) {
              location += `, ${address.zip}`;
            }
            if (!!address.city) {
              location += `, ${address.city}`;
            }

            installationData = {
              gatewaySerial: '',
              id: matchingInstallation.id,
              image: matchingDevice?.materialImage || matchingInstallation?.device?.materialImage,
              location,
              marketingName:
                matchingDevice?.marketingName || matchingInstallation?.device?.marketingName,
              ownerName:
                matchingInstallation.owner.firstName + ' ' + matchingInstallation.owner.lastName,
              recommendations: optimization.recommendations.map(parseRecommendation),
            };
          }

          // Old installation list loop
        } else if (allInstallations.length > 0) {
          let mainDevice: InstallationItemGatewayDevice;

          const matchingInstallation = allInstallations.find((installation) =>
            installation.gateways.some((gateway) => {
              mainDevice = gateway.devices.find(
                (device) => device.serial === optimization.boilerSerial
              );

              return !!mainDevice;
            })
          );

          if (matchingInstallation) {
            const { id, location, ownerName } = matchingInstallation;
            const { marketingName, image, imageUrl, gatewaySerial } = mainDevice;
            installationData = {
              id,
              gatewaySerial,
              image: imageUrl || (image ? `/assets/img/devices/${image}.png` : null),
              location,
              marketingName,
              ownerName,
              recommendations: optimization.recommendations.map(parseRecommendation),
            };
          }
        }

        if (installationData) {
          return [
            ...acc,
            {
              ...installationData,
            },
          ];
        } else {
          return acc;
        }
      }, [])
  );

  export const shouldFetchOptimizationRecommendations = createSelector(
    viguideRecommendationsState,
    AuthSelectors.isRsaUser(),
    (state, isRsaUser): boolean => !state.loaded && !state.error && !state.loading && !isRsaUser
  );

  function parseRecommendation(
    recommendation: ViguideRecommendationData
  ): OptimizationRecommendationSpreaded {
    const hcRegex = /^hc(\d)(.+)/;
    const [_, hc, translationSlug] = recommendation.name.match(hcRegex) ?? [];

    return {
      hc,
      translationSlug: translationSlug ?? recommendation.name,
      type: recommendation.name,
    };
  }
}
